import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Megaphone, Repeat2, XCircle } from "lucide-react";
import api, { fmtIDR } from "../../lib/api";
import { inputCls, errMsg } from "../admin/ui";
import { StatusBadge, ProgressBar, MODES, fmtQty, perLabel } from "./shared";

const STATUSES = [["", "Semua"], ["draft", "Draft"], ["open", "Open"], ["filled", "Filled"], ["closed", "Ditutup"]];

export async function publish(d, after) {
  try {
    const r = await api.post(`/demands/${d.id}/publish`);
    const n = r.data.notify_result || {};
    toast.success(`${d.job_no} dipublikasikan. Notifikasi website: ${n.website || 0}, Telegram antre: ${n.telegram_queued || 0}${n.invited_website != null && (n.invited_website || n.invited_telegram_queued) ? `, undangan khusus: ${n.invited_website + n.invited_telegram_queued}` : ""}`);
    after?.();
  } catch (e) { toast.error(errMsg(e)); }
}

export async function repost(d, after) {
  try {
    const r = await api.post(`/demands/${d.id}/repost`);
    toast.success(`${d.job_no} diposting ke channel Telegram (total ${r.data.telegram_posts_count} postingan)`);
    after?.();
  } catch (e) { toast.error(errMsg(e)); }
}

export async function closeDemand(d, after) {
  const n = d.telegram_posts_count || 0;
  if (!window.confirm(`Tutup demand ${d.job_no}?${n ? `\n${n} postingan Telegram akan diedit menjadi ❌ DEMAND CLOSED / SOLD OUT.` : ""}`)) return;
  try {
    const r = await api.post(`/demands/${d.id}/close`, { reason: "" });
    const t = r.data.telegram_edit || {};
    toast.success(`${d.job_no} ditutup${n ? ` · Telegram diedit ${t.telegram_edited || 0}/${n}` : ""}`);
    if (t.telegram_failed) toast.error(`${t.telegram_failed} postingan Telegram gagal diedit (mungkin sudah dihapus dari channel)`);
    after?.();
  } catch (e) { toast.error(errMsg(e)); }
}

function Row({ d, onOpen, reload }) {
  return (
    <tr data-testid={`demand-row-${d.id}`} className="border-b border-[#35353B] hover:bg-[#191A1F] transition-colors">
      <td className="py-3 pr-3"><button data-testid={`demand-open-${d.id}`} onClick={() => onOpen(d.id)} className="font-mono text-xs text-[#D4AF37] hover:underline">{d.job_no}</button>
        <div className="text-[#F5F3ED] text-sm">WTB {d.item_name}</div></td>
      <td className="py-3 pr-3 text-xs text-[#A8A8AD]">{d.game_name}<div>{d.server_name}{d.faction ? ` · ${d.faction}` : ""}</div></td>
      <td className="py-3 pr-3 text-right text-xs whitespace-nowrap"><div className="text-[#F5F3ED]">{fmtQty(d.taken_qty)} / {fmtQty(d.target_qty)}</div>
        <div className="w-28 ml-auto mt-1"><ProgressBar d={d} /></div><div data-testid={`demand-remaining-${d.id}`} className="text-[#A8A8AD] mt-1">Sisa {fmtQty(d.rule.remaining)}</div></td>
      <td className="py-3 pr-3 text-right text-xs whitespace-nowrap text-[#D4AF37]">{fmtIDR(d.price_per_unit_idr)}<span className="text-[#A8A8AD]">{perLabel(d.price_per_qty)}</span>
        <div className="text-[#A8A8AD]">{MODES[d.take_mode].label}{d.min_take ? ` · min ${fmtQty(d.min_take)}` : ""}</div></td>
      <td className="py-3 pr-3 text-right text-xs text-[#F5F3ED] whitespace-nowrap">{fmtIDR(d.allocated_value_idr)}<div className="text-[#A8A8AD]">{d.allocations_count} supplier</div></td>
      <td className="py-3 pr-3"><StatusBadge status={d.status} testId={`demand-status-${d.id}`} /></td>
      <td className="py-3 text-right whitespace-nowrap">
        {d.status === "draft" && <button data-testid={`demand-publish-${d.id}`} onClick={() => publish(d, reload)}
          className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] font-bold rounded-sm px-3 py-1.5 text-xs mr-2"><Megaphone size={13} /> CARI SELLER</button>}
        {d.status === "open" && <button data-testid={`demand-repost-${d.id}`} onClick={() => repost(d, reload)} title="Posting ulang ke channel Telegram"
          className="inline-flex items-center gap-1 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] rounded-sm px-2.5 py-1.5 text-xs mr-2 transition-colors"><Repeat2 size={13} /> Repost{d.telegram_posts_count ? ` · ${d.telegram_posts_count}` : ""}</button>}
        {["open", "filled"].includes(d.status) && <button data-testid={`demand-close-${d.id}`} onClick={() => closeDemand(d, reload)} title="Tutup Demand"
          className="inline-flex items-center gap-1 border border-[#F06464] text-[#F06464] hover:bg-[#F06464] hover:text-[#101114] rounded-sm px-2.5 py-1.5 text-xs mr-2 transition-colors"><XCircle size={13} /> Tutup</button>}
        <button onClick={() => onOpen(d.id)} className="text-xs text-[#D4AF37] hover:underline">Detail</button>
      </td>
    </tr>
  );
}

export default function DemandList({ games, servers, refresh, onOpen }) {
  const [f, setF] = useState({ q: "", game_id: "", server_id: "", status: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 20, status_counts: {} });
  const load = () => api.get("/demands", { params: { ...f, page } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [f.game_id, f.server_id, f.status, page, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(); }, 350);
    return () => clearTimeout(t);
  }, [f.q]); // eslint-disable-line react-hooks/exhaustive-deps
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const sel = (k) => (e) => { setPage(1); setF({ ...f, [k]: e.target.value, ...(k === "game_id" ? { server_id: "" } : {}) }); };

  return (
    <div data-testid="demand-list" className="bg-[#202127] border border-[#35353B] rounded-lg p-4 sm:p-6">
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map(([k, l]) => (
          <button key={k || "all"} data-testid={`demand-filter-status-${k || "all"}`} onClick={() => { setPage(1); setF({ ...f, status: k }); }}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${f.status === k ? "bg-[#D4AF37] text-[#101114] border-[#D4AF37]" : "border-[#35353B] text-[#A8A8AD] hover:border-[#D4AF37]"}`}>
            {l}{k && data.status_counts[k] ? ` · ${data.status_counts[k]}` : ""}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); setPage(1); load(); }} className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div className="relative sm:col-span-2"><Search size={14} className="absolute left-3 top-[1.15rem] text-[#A8A8AD]" />
          <input data-testid="demand-search-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cari Job ID, item, game, server..." className={`${inputCls} !mt-2 pl-9`} /></div>
        <select data-testid="demand-filter-game" value={f.game_id} onChange={sel("game_id")} className={inputCls}><option value="">Semua game</option>{games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
        <select data-testid="demand-filter-server" value={f.server_id} onChange={sel("server_id")} className={inputCls}><option value="">Semua server</option>
          {servers.filter((s) => !f.game_id || s.game_id === f.game_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
      </form>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]">
            <th className="py-2 pr-3">Job</th><th className="py-2 pr-3">Game / Server</th><th className="py-2 pr-3 text-right">Terambil / Target</th><th className="py-2 pr-3 text-right">Harga · Mode</th>
            <th className="py-2 pr-3 text-right">Nilai Alokasi</th><th className="py-2 pr-3">Status</th><th className="py-2"></th></tr></thead>
          <tbody>
            {data.items.map((d) => <Row key={d.id} d={d} onOpen={onOpen} reload={load} />)}
            {data.items.length === 0 && <tr><td colSpan="7" className="py-12 text-center text-[#A8A8AD]">Belum ada demand</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-xs text-[#A8A8AD]">
        <span data-testid="demand-total">{data.total} demand</span>
        <div className="flex items-center gap-2">
          <button data-testid="demand-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border border-[#35353B] rounded-sm disabled:opacity-30">‹</button>
          <span data-testid="demand-page">{page} / {pages}</span>
          <button data-testid="demand-next-page" disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border border-[#35353B] rounded-sm disabled:opacity-30">›</button>
        </div>
      </div>
    </div>
  );
}
